import { useState, useEffect, useRef } from 'react';
import { Loader2, CheckCircle2, XCircle, FileText, X, ChevronDown } from 'lucide-react';
import type { ActiveResearchTask } from './canvas-types';

interface TaskBottomBarProps {
  task: ActiveResearchTask;
  onViewReport: () => void;
  onDismiss: () => void;
  onScrollToProgress?: () => void;
  conversationId?: number | null;
}

function formatElapsed(startedAt: number): string {
  const seconds = Math.floor((Date.now() - startedAt) / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export function TaskBottomBar({ task, onViewReport, onDismiss, onScrollToProgress, conversationId }: TaskBottomBarProps) {
  const { plan, status, completedSteps, totalSteps, documentReady, startedAt } = task;
  const [elapsed, setElapsed] = useState<string>('0s');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isRunning = status === 'running' || status === 'planning';
  const isFailed = status === 'failed';
  const isCompleted = status === 'completed';

  const runningStep = plan.steps.find((s) => s.status === 'running');

  useEffect(() => {
    if (!startedAt || !isRunning) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    setElapsed(formatElapsed(startedAt));
    timerRef.current = setInterval(() => setElapsed(formatElapsed(startedAt)), 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startedAt, isRunning]);

  if (status === 'completed' && !documentReady) return null;

  return (
    <div
      data-testid="task-bottom-bar"
      style={{
        position: 'relative',
        background: 'var(--color-background-primary, #ffffff)',
        border: '1px solid var(--color-border-tertiary, #e5e7eb)',
        borderRadius: 10,
        margin: '4px 0 8px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
        }}
      >
        {/* Status icon — muted gray */}
        {isRunning && (
          <Loader2
            size={14}
            data-testid="bottom-bar-icon-running"
            style={{ color: 'var(--color-text-tertiary, #9ca3af)', animation: 'spin 1s linear infinite', flexShrink: 0 }}
          />
        )}
        {isCompleted && (
          <CheckCircle2 size={14} data-testid="bottom-bar-icon-completed" style={{ color: 'var(--color-text-tertiary, #9ca3af)', flexShrink: 0 }} />
        )}
        {isFailed && (
          <XCircle size={14} data-testid="bottom-bar-icon-failed" style={{ color: '#dc2626', flexShrink: 0 }} />
        )}

        {/* Text area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: isFailed ? '#dc2626' : 'var(--color-text-primary, #1f2937)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {isRunning && runningStep ? runningStep.description || plan.title : plan.title}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {isRunning && (
              <span style={{ fontSize: 11, color: 'var(--color-text-tertiary, #9ca3af)' }}>
                {completedSteps}/{totalSteps} steps · {elapsed}
              </span>
            )}
            {isCompleted && (
              <span style={{ fontSize: 11, color: 'var(--color-text-tertiary, #9ca3af)' }}>
                {completedSteps} steps completed{startedAt ? ` · ${formatElapsed(startedAt)}` : ''}
              </span>
            )}
            {isFailed && (
              <span style={{ fontSize: 11, color: '#dc2626' }}>
                {task.errorMessage || 'Research failed'}
              </span>
            )}
          </div>
        </div>

        {/* Scroll to progress chevron */}
        {onScrollToProgress && isRunning && (
          <button
            data-testid="btn-task-bar-scroll-to"
            onClick={onScrollToProgress}
            title="Scroll to progress"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              border: '1px solid var(--color-border-tertiary, #e5e7eb)',
              borderRadius: 4,
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--color-text-tertiary, #9ca3af)',
              flexShrink: 0,
            }}
          >
            <ChevronDown size={12} />
          </button>
        )}

        {/* View report — text link only */}
        {documentReady && (
          <button
            data-testid="btn-task-bar-view-report"
            onClick={onViewReport}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '0 4px',
              background: 'transparent',
              color: '#4f46e5',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 500,
              flexShrink: 0,
              textDecoration: 'none',
            }}
          >
            <FileText size={12} />
            View report
          </button>
        )}

        {/* Dismiss — scoped to conversationId */}
        <button
          data-testid="btn-task-bar-dismiss"
          onClick={onDismiss}
          title="Dismiss"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            border: 'none',
            borderRadius: 4,
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--color-text-tertiary, #9ca3af)',
            flexShrink: 0,
          }}
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}
